import { useTranslation } from 'react-i18next';
import { useNavigate, type ErrorComponentProps } from '@tanstack/react-router';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { describeError } from '@/lib/errorMessage';
import { LocaleSwitcher } from '@/components/layout/LocaleSwitcher';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

export function ErrorBoundaryFallback({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const message = describeError(t, error);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--color-bg)] p-6">
      <div className="absolute end-4 top-4 flex gap-2">
        <LocaleSwitcher />
        <ThemeToggle />
      </div>
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>{t('errors.title')}</CardTitle>
          <CardDescription>{t('errors.body')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-[var(--radius-md)] bg-[color:var(--color-surface-2)] p-3 text-xs text-[color:var(--color-fg-muted)]">
            {message}
          </pre>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => reset()}>{t('errors.refresh')}</Button>
            <Button variant="ghost" onClick={() => void navigate({ to: '/dashboard' })}>
              {t('errors.back')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
